import {
  Box,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '../../utils/formatters';
import { useOnboardingLens } from './onboardingLens';
import { useOnboardingNestedDialog } from './useOnboardingNestedDialog';

export type OnboardingLogoPreviewTarget = {
  url: string;
  name: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
};

interface OnboardingLogoPreviewDialogProps {
  open: boolean;
  target: OnboardingLogoPreviewTarget | null;
  onClose: () => void;
}

/** Figma — logo image preview popup on Step 5 thumbnail click. */
export function OnboardingLogoPreviewDialog({
  open,
  target,
  onClose,
}: OnboardingLogoPreviewDialogProps) {
  const { t } = useTranslation();
  const { layout, typography, colors } = useOnboardingLens();
  const { formField, formSection, dialog } = layout;
  const nestedDialog = useOnboardingNestedDialog();

  if (!target) return null;

  const metaParts: string[] = [];
  if (target.width != null && target.height != null) {
    metaParts.push(`${target.width} × ${target.height} px`);
  }
  if (target.sizeBytes != null) {
    metaParts.push(formatFileSize(target.sizeBytes));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disablePortal={nestedDialog.disablePortal}
      container={nestedDialog.container}
      sx={nestedDialog.sx}
      aria-labelledby="onboarding-logo-preview-title"
      slotProps={{
        root: {
          sx: nestedDialog.nested ? undefined : { zIndex: nestedDialog.rootZIndex },
        },
        backdrop: {
          sx: {
            backgroundColor: colors.backgroundBackdrop,
          },
        },
        paper: {
          sx: {
            width: `min(calc(${dialog.width} - ${formSection.sectionGap}), calc(100vw - ${dialog.margin} * 2))`,
            borderRadius: formField.inputRadius,
            overflow: 'hidden',
            boxShadow: dialog.shadow,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: formSection.sectionGap,
          px: formSection.sectionGap,
          pt: formSection.sectionGap,
          pb: formField.labelToInputGap,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="onboarding-logo-preview-title"
            sx={{
              ...typography.bodyEmphasis,
              color: colors.typeDefault,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {target.name}
          </Typography>
          {metaParts.length > 0 && (
            <Typography
              sx={{
                mt: formField.labelGap,
                ...typography.textMd,
                color: colors.typeMuted,
              }}
            >
              {metaParts.join(' • ')}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={t('onboarding.wizard.step6.logo_preview_close')}
          onClick={onClose}
          size="small"
          sx={{ flexShrink: 0, color: colors.typeDefault }}
        >
          <CloseIcon size="md" />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: formSection.sectionGap,
          pb: formSection.sectionGap,
          pt: formField.labelToInputGap,
        }}
      >
        <Box
          sx={{
            bgcolor: colors.surfaceVariant,
            borderRadius: formField.inputRadius,
            minHeight: `calc(${layout.complete.illustrationHeight} * 2 + ${formSection.sectionGap})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: formSection.sectionGap,
          }}
        >
          <Box
            component="img"
            src={target.url}
            alt={target.name}
            sx={{
              maxWidth: '100%',
              maxHeight: `calc(${layout.complete.illustrationHeight} + ${formSection.sectionGap})`,
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}
